import type { FeatureVendor } from '~/models/gdpr-publisher-selection'

export const useUserCentrics = createSharedComposable(() => {
  const { userCentricsId } = useEnvs()
  const uc = ref()
  const ucServices = ref([])
  const selectedPublishers: ComputedRef<FeatureVendor[]> = computed(_ => ucServices.value.filter((s: { consent: { status: boolean } }) => s.consent.status))

  // Store cleanup functions
  const eventListeners: Array<() => void> = []
  let shadowObserver: MutationObserver | null = null
  let rootObserver: MutationObserver | null = null

  const initUC = () => {
    registerUserCentricsEventListeners()
    loadUserCentricsScript()
    observeUsercentricsRoot()
  }

  const refreshServices = () => {
    set(ucServices, uc.value.getServicesBaseInfo())
    createLegacyCookie()
  }

  const showConsent = () => {
    uc.value.showSecondLayer()
  }

  const showFirstLayer = () => {
    uc.value.showFirstLayer()
  }

  // TODO: Remove after full migration to FEC2.0
  const createLegacyCookie = () => {
    const vendorCookies: string[] = []
    const normalizeVendorName = (vendorName: string) => vendorName.replace(/[\s,\.]/g, '')
    selectedPublishers.value.forEach((publisher) => {
      vendorCookies.push(`${normalizeVendorName(publisher.name)}Cookie`)
    })
    localStorage.setItem(
      'VendorsCookies',
      JSON.stringify(vendorCookies),
    )
  }

  function waitForUCInitialization(): Promise<void> {
    return new Promise<void>((resolve) => {
      const eventListener = () => {
        window.removeEventListener(UCEvents.UC_UI_INITIALIZED, eventListener)
        resolve()
      }

      window.addEventListener(UCEvents.UC_UI_INITIALIZED, eventListener)
    })
  }

  function closeCMP() {
    uc.value.closeCMP()
  }

  const registerUserCentricsEventListeners = () => {
    const initializedHandler = () => {
      set(uc, window.UC_UI)
      refreshServices()
      const googleAnalyticsTracking = useGoogleAnalytics()
      googleAnalyticsTracking.setGoogleAdsConsent()
    }

    const cmpEventHandler = (e: Event) => {
      const event = e as UserCentricsEvent

      if (event.detail && (event.detail.type === UCEvents.SAVE || event.detail.type === UCEvents.ACCEPT_ALL)) {
        refreshServices()
        const isGoogleAdsAllowed = selectedPublishers.value.find(p => p.name === 'Google Ads')
        const isGoogleAnalyticsAllowed = selectedPublishers.value.find(p => p.name === 'Google Analytics')
        const googleAnalyticsTracking = useGoogleAnalytics()
        googleAnalyticsTracking.setGoogleAdsConsent({
          adStorage: !!isGoogleAdsAllowed,
          userData: !!isGoogleAdsAllowed,
          personalization: !!isGoogleAdsAllowed,
          analyticsStorage: !!isGoogleAnalyticsAllowed,
        })
      }
    }

    window.addEventListener(UCEvents.UC_UI_INITIALIZED, initializedHandler)
    window.addEventListener(UCEvents.UC_UI_CMP_EVENT, cmpEventHandler)

    // Store cleanup functions
    eventListeners.push(
      () => window.removeEventListener(UCEvents.UC_UI_INITIALIZED, initializedHandler),
      () => window.removeEventListener(UCEvents.UC_UI_CMP_EVENT, cmpEventHandler),
    )
  }

  const { load: loadUserCentricsScript } = useScriptTag(
    'https://app.usercentrics.eu/browser-ui/latest/loader.js',
    undefined,
    {
      manual: true,
      async: true,
      attrs: {
        'id': 'usercentrics-cmp',
        'data-settings-id': userCentricsId.value,
        'data-tcf-enabled': 'true',
      },
    },
  )

  const observeUsercentricsRoot = () => {
    if (rootObserver) {
      rootObserver.disconnect()
      rootObserver = null
    }

    rootObserver = new MutationObserver(() => {
      const usercentricsRoot = document.querySelector('#usercentrics-root')
      if (usercentricsRoot && usercentricsRoot.shadowRoot) {
        observeShadowDOM(usercentricsRoot.shadowRoot)
        rootObserver?.disconnect()
        rootObserver = null
      }
    })

    if (typeof document !== 'undefined' && document.body) {
      rootObserver.observe(document.body, { childList: true, subtree: true })
    }
  }

  const observeShadowDOM = (shadowRoot: any) => {
    if (shadowObserver) {
      shadowObserver.disconnect()
    }

    shadowObserver = new MutationObserver(() => {
      modifyShadowContent(shadowRoot)
    })

    shadowObserver.observe(shadowRoot, { childList: true, subtree: true })
  }

  const cleanup = () => {
    eventListeners.forEach(cleanupFn => cleanupFn())
    eventListeners.length = 0

    if (rootObserver) {
      rootObserver.disconnect()
      rootObserver = null
    }

    if (shadowObserver) {
      shadowObserver.disconnect()
      shadowObserver = null
    }
  }

  // Automatically cleanup when the scope is disposed (app unmount)
  tryOnScopeDispose(cleanup)

  const modifyShadowContent = (shadowRoot: any) => {
    const acceptButton = shadowRoot.querySelector('[data-testid="uc-more-button"]')
    if (acceptButton) {
      acceptButton.setAttribute('aria-label', 'Einstellungen')
    }
  }

  return {
    uc,
    initUC,
    refreshServices,
    selectedPublishers,
    showConsent,
    showFirstLayer,
    waitForUCInitialization,
    UCEvents,
    closeCMP,
    cleanup,
  }
})

export interface UserCentricsEvent extends Event {
  detail: {
    type: string
  }
}

enum UCEvents {
  UC_UI_INITIALIZED = 'UC_UI_INITIALIZED',
  UC_UI_CMP_EVENT = 'UC_UI_CMP_EVENT',
  SAVE = 'SAVE',
  ACCEPT_ALL = 'ACCEPT_ALL',
}
